import { useState, useEffect } from "react";
import { LoginDialog } from "./LoginDialog";
import { SignUpDialog } from "./SignUpDialog";
import { ResourceUsage } from "./navigation/ResourceUsage";
import { NavigationLinks } from "./navigation/NavigationLinks";
import { AuthButtons } from "./navigation/AuthButtons";
import { PricingDialog } from "./PricingDialog";
import { useSession } from "@/hooks/useSession";
import { useProfile } from "@/hooks/useProfile";
import { getMaxResources } from "@/utils/subscriptionLimits";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "./ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import type { SubscriptionTier } from "@/hooks/useProfile";

const getValidSubscriptionTier = (tier: string | null | undefined): SubscriptionTier => {
  if (!tier || tier === 'trainee') return 'free';
  return tier as SubscriptionTier;
};

export const Navigation = () => {
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const { session, isSessionLoading } = useSession();
  const { data: profile } = useProfile(session?.user?.id);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const isAuthenticated = !isSessionLoading && !!session;
  const isTrainee = profile?.role === 'trainee';
  const subscriptionTier = getValidSubscriptionTier(profile?.subscription_tier);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isSessionLoading) {
      const isProtectedRoute = ['/training', '/account'].some(route => 
        location.pathname.startsWith(route)
      );
      
      if (isProtectedRoute && !isAuthenticated) {
        navigate('/', { replace: true });
      }
    }
  }, [isSessionLoading, isAuthenticated, location.pathname, navigate]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <nav className="container mx-auto px-4">
        <div className="flex h-14 items-center justify-between">
          <NavigationLinks session={session} />
          
          {isMobile ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden"
            >
              <Menu className="h-5 w-5" />
            </Button>
          ) : (
            <div className="flex items-center gap-4">
              {isAuthenticated && profile && !isTrainee && (
                <ResourceUsage
                  usedResources={profile.resources_used_this_month || 0}
                  maxResources={getMaxResources(subscriptionTier)}
                  nextResetDate={new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() + 1,
                    1
                  ).toLocaleDateString()}
                  onUpgradeClick={() => setIsPricingOpen(true)}
                />
              )}
              
              <AuthButtons
                isLoggedIn={isAuthenticated}
                onLoginClick={() => setIsLoginOpen(true)}
                onSignUpClick={() => setIsSignUpOpen(true)}
              />
            </div>
          )}
        </div>

        {isMobile && isMobileMenuOpen && (
          <div className="border-t py-4 space-y-4 md:hidden">
            {isAuthenticated && profile && !isTrainee && (
              <ResourceUsage
                usedResources={profile.resources_used_this_month || 0}
                maxResources={getMaxResources(subscriptionTier)}
                nextResetDate={new Date(
                  new Date().getFullYear(),
                  new Date().getMonth() + 1,
                  1
                ).toLocaleDateString()}
                onUpgradeClick={() => {
                  setIsPricingOpen(true);
                  setIsMobileMenuOpen(false);
                }}
              />
            )}
            
            <div className="flex flex-col gap-2">
              <AuthButtons
                isLoggedIn={isAuthenticated}
                onLoginClick={() => {
                  setIsLoginOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                onSignUpClick={() => {
                  setIsSignUpOpen(true);
                  setIsMobileMenuOpen(false);
                }}
              />
            </div>
          </div>
        )}
      </nav>

      <LoginDialog 
        open={isLoginOpen} 
        onOpenChange={setIsLoginOpen} 
        onSignUpClick={() => {
          setIsLoginOpen(false);
          setTimeout(() => setIsSignUpOpen(true), 100);
        }}
      />
      <SignUpDialog open={isSignUpOpen} onOpenChange={setIsSignUpOpen} />
      {profile && !isTrainee && (
        <PricingDialog 
          open={isPricingOpen} 
          onOpenChange={setIsPricingOpen}
          currentTier={subscriptionTier}
        />
      )}
    </header>
  );
};
